import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const OpenapiStudioPanel = ({ className, onAmbiente }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [conf, setConf] = useState("");
  const set = (d) => { setF(d); onAmbiente?.(d.ambiente); };
  useEffect(() => { api.get("/admin/openapi").then((r) => set(r.data)).catch(() => setF(false)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const toggle = async (v) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/openapi", { attivo: v }); set(data); toast.success(v ? "Openapi attivo: connessione verificata" : "Openapi disattivato"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cambia = async (ambiente) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/openapi/ambiente", { ambiente, conferma: conf });
      set(data); setAsk(false); setConf("");
      toast.success(ambiente === "produzione" ? "Ambiente reale attivo: riattiva le aziende con il tasto Openapi" : "Tornato all'ambiente di prova: riattiva le aziende con il tasto Openapi", { duration: 8000 });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Openapi · invio, firma e conservazione (SdI)" className={className}>
      <label className="flex flex-wrap items-center gap-3 text-sm">
        <Switch data-testid="openapi-enabled-switch" disabled={busy || !f.token_set} checked={f.enabled} onCheckedChange={toggle} />
        {f.enabled ? "Collegamento Openapi attivo" : "Collegamento Openapi non attivo"}
        <span data-testid="openapi-ambiente" className={`rounded px-2 py-0.5 text-xs font-semibold ${f.ambiente === "sandbox" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{f.ambiente === "sandbox" ? "PROVA (sandbox)" : "PRODUZIONE"}</span>
        {f.ambiente === "sandbox"
          ? <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy} data-testid="openapi-go-produzione" onClick={() => setAsk(true)}>Passa all'ambiente reale</Button>
          : <Button size="sm" variant="outline" disabled={busy} data-testid="openapi-go-sandbox" onClick={() => cambia("sandbox")}>Torna all'ambiente di prova</Button>}
      </label>
      {!f.token_set && <p data-testid="openapi-token-missing" className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">Credenziali Openapi non ancora inserite: chiedile all'assistente per attivare il collegamento.</p>}
      <p className="mt-3 text-xs text-slate-600">Con un unico account dello studio. Attivalo poi azienda per azienda in elenco (tasto Openapi): le fatture vengono firmate, inviate allo SdI e conservate a norma. Dopo un cambio di ambiente ogni azienda va riattivata con il tasto Openapi. Le aziende devono registrare il codice destinatario JKKZDGR sul sito dell'Agenzia delle Entrate per ricevere le fatture passive.</p>
      <Dialog open={ask} onOpenChange={(o) => { setAsk(o); setConf(""); }}>
        <DialogContent data-testid="openapi-produzione-dialog">
          <DialogHeader>
            <DialogTitle>Passare all'ambiente reale?</DialogTitle>
            <DialogDescription>Le fatture verranno inviate davvero allo SdI, con valore fiscale e a pagamento sul tuo account Openapi. Le aziende andranno riattivate con il tasto Openapi. Scrivi <b>ATTIVA</b> per confermare.</DialogDescription>
          </DialogHeader>
          <Input data-testid="openapi-conferma-input" value={conf} onChange={(e) => setConf(e.target.value)} placeholder="ATTIVA" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsk(false)}>Annulla</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="openapi-conferma-button" disabled={busy || conf.trim().toUpperCase() !== "ATTIVA"} onClick={() => cambia("produzione")}>Passa all'ambiente reale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};

export const SdiPanel = ({ className }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/company/sdi").then((r) => setF(r.data)).catch(() => setF(false));
  useEffect(() => { load(); }, []);
  if (!f) return null;
  const sync = async () => {
    setBusy(true);
    try { await api.post("/company/sdi/sync"); toast.success("Sincronizzazione avviata: esiti e fatture ricevute compariranno tra qualche minuto"); setTimeout(load, 15000); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Invio SdI tramite Openapi" className={className}>
      <div data-testid="sdi-in-uso" className={`rounded-md px-3 py-2 text-sm ${f.openapi ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
        {f.openapi ? `In uso: Openapi dello Studio Principale (firma e conservazione) · ${f.sandbox ? "PROVA sandbox" : "PRODUZIONE"}` : "Openapi non ancora attivo per questa azienda: gli invii sono simulati. Lo attiva lo Studio Principale."}
      </div>
      {f.openapi && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span data-testid="sdi-last-sync">Ultima sincronizzazione: {f.last_sync ? new Date(f.last_sync).toLocaleString("it-IT") : "mai"}</span>
          {f.last_error && <span data-testid="sdi-last-error" className="text-rose-700">Errore: {f.last_error}</span>}
          <Button size="sm" variant="outline" data-testid="sdi-sync-button" disabled={busy} onClick={sync}><RefreshCw className="mr-1 h-4 w-4" />Sincronizza ora</Button>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-600">Esiti SdI e fatture ricevute si aggiornano da soli. Per ricevere le fatture passive registra il codice destinatario JKKZDGR sul sito dell'Agenzia delle Entrate.</p>
    </Panel>
  );
};
